 document.addEventListener('DOMContentLoaded', function () {

// Global variables
let swiperInstance = null;
const swiperContainer = document.querySelector('.product-media-display');
if (!swiperContainer) return;

// Zoom only scales the photo already on screen (a CSS transform), so the
// browser never fetches a bigger file by itself. On the first zoom, say the
// photo is shown at the largest zoom (3x) so the srcset gives the sharpest
// file (up to the original upload) once, instead of a blurry upscale.
const MAX_ZOOM = 3;
function sharpenZoomedImage(slide) {
  const img = slide && slide.querySelector('img.product-image');
  if (!img || !img.getAttribute('srcset')) return;
  const need = Math.ceil(img.offsetWidth * MAX_ZOOM);
  // Only ever raise it (e.g. zoomed again in fullscreen, where tiles are bigger).
  if (need <= Number(img.dataset.zoomSize || 0)) return;
  img.dataset.zoomSize = need;

  // The srcset width the browser will now pick: the smallest that covers the
  // zoomed size on this screen, else the largest (the original upload).
  const candidates = img.getAttribute('srcset').split(',').map((entry) => {
    const [url, w] = entry.trim().split(/\s+/);
    return { url, width: parseInt(w, 10) };
  }).sort((a, b) => a.width - b.width);
  const target = candidates.find((c) => c.width >= need * window.devicePixelRatio) || candidates[candidates.length - 1];
  const currentWidth = parseInt(((img.currentSrc || img.src).match(/[?&]width=(\d+)/) || [])[1], 10);

  // Nothing new to download: just record the size.
  if (!currentWidth || target.width <= currentWidth) {
    img.setAttribute('sizes', `${need}px`);
    return;
  }

  // Smooth swap: keep the softer zoomed photo on screen with the loading bar
  // (slider-bar colours) while the sharp file downloads and decodes off
  // screen, then fade it in exactly over the photo (inside the same <picture>,
  // so it follows zoom and drag), switch the photo itself to the now-cached
  // file and drop the overlay.
  slide.classList.add('is-sharpening');
  const sharp = new Image();
  sharp.decoding = 'async';
  sharp.alt = '';
  sharp.setAttribute('aria-hidden', 'true');
  sharp.sizes = `${need}px`;
  sharp.srcset = img.getAttribute('srcset');

  // Wait for the download; give decode() a short head start so the fade
  // doesn't begin on an undecoded frame (some browsers never settle decode()
  // for an image that isn't on the page, hence the cap).
  const loaded = new Promise((resolve, reject) => {
    if (sharp.complete && sharp.naturalWidth) resolve();
    sharp.addEventListener('load', resolve, { once: true });
    sharp.addEventListener('error', reject, { once: true });
  });
  loaded.then(() => Promise.race([
    sharp.decode().catch(() => {}),
    new Promise((resolve) => setTimeout(resolve, 300)),
  ])).then(() => {
    const picture = img.parentElement;
    const fit = getComputedStyle(img);
    sharp.className = 'pdp-zoom-sharp';
    Object.assign(sharp.style, {
      left: `${img.offsetLeft}px`,
      top: `${img.offsetTop}px`,
      width: `${img.offsetWidth}px`,
      height: `${img.offsetHeight}px`,
      objectFit: fit.objectFit,
      objectPosition: fit.objectPosition,
    });
    picture.appendChild(sharp);
    sharp.getBoundingClientRect(); // start the fade from opacity 0
    sharp.classList.add('is-visible');

    const finish = () => {
      clearTimeout(fadeTimer);
      slide.classList.remove('is-sharpening');
      // The photo now picks the same, already-downloaded file; keep the
      // overlay until it has actually switched so the soft image never flashes.
      const removeOverlay = () => {
        clearTimeout(removeTimer);
        sharp.remove();
      };
      const removeTimer = setTimeout(removeOverlay, 1000);
      img.addEventListener('load', removeOverlay, { once: true });
      img.setAttribute('sizes', `${need}px`);
    };
    const fadeTimer = setTimeout(finish, 600);
    sharp.addEventListener('transitionend', finish, { once: true });
  }).catch(() => {
    slide.classList.remove('is-sharpening');
    img.setAttribute('sizes', `${need}px`);
  });
}

// Placeholder photos (past the grid's View More count) get their real
// src/srcset only when they're needed: slider, View More or fullscreen.
function loadPlaceholderImages(lazy) {
  swiperContainer.querySelectorAll('.lazy-load-desktop[data-desktop-src]').forEach((img) => {
    if (img.dataset.desktopSrcset) img.srcset = img.dataset.desktopSrcset;
    img.src = img.dataset.desktopSrc;
    img.removeAttribute('data-desktop-src');
    img.removeAttribute('data-desktop-srcset');
    img.classList.add('loaded');
    img.classList.remove('lazy-load-desktop');
    if (lazy) img.loading = 'lazy';
  });
}

// Gallery mode comes from the same media query the CSS uses for slider mode
// (pdp-product.css), so CSS switches the layout and JS only follows it.
// Spec: documents/PDP-STAGING-LOG.md section 8.
// Generated list, identical to the slider query in pdp-product.css (staging D3,
// regenerate both together: documents/PDP-STAGING-LOG.md 8.9).
const sliderMedia = window.matchMedia(
  '(max-width: 519.98px), (min-width: 520px) and (max-width: 539.98px) and (min-height: 323.02px), (min-width: 540px) and (max-width: 559.98px) and (min-height: 348.02px), (min-width: 560px) and (max-width: 579.98px) and (min-height: 373.02px), (min-width: 580px) and (max-width: 599.98px) and (min-height: 398.02px), (min-width: 600px) and (max-width: 619.98px) and (min-height: 423.02px), (min-width: 620px) and (max-width: 639.98px) and (min-height: 448.02px), (min-width: 640px) and (max-width: 659.98px) and (min-height: 473.02px), (min-width: 660px) and (max-width: 679.98px) and (min-height: 498.02px), (min-width: 680px) and (max-width: 699.98px) and (min-height: 523.02px), (min-width: 700px) and (max-width: 719.98px) and (min-height: 548.02px), (min-width: 720px) and (max-width: 739.98px) and (min-height: 573.02px), (min-width: 520px) and (max-width: 539.98px) and (max-height: 323px), (min-width: 540px) and (max-width: 559.98px) and (max-height: 348px), (min-width: 560px) and (max-width: 579.98px) and (max-height: 373px), (min-width: 580px) and (max-width: 599.98px) and (max-height: 398px), (min-width: 600px) and (max-width: 619.98px) and (max-height: 423px), (min-width: 620px) and (max-width: 639.98px) and (max-height: 448px), (min-width: 640px) and (max-width: 659.98px) and (max-height: 473px), (min-width: 660px) and (max-width: 679.98px) and (max-height: 498px), (min-width: 680px) and (max-width: 699.98px) and (max-height: 523px), (min-width: 700px) and (max-width: 719.98px) and (max-height: 548px), (min-width: 720px) and (max-width: 739.98px) and (max-height: 573px), (min-width: 740px) and (max-width: 749.98px) and (max-height: 485.98px), (min-width: 750px) and (max-width: 759.98px) and (max-height: 497.98px), (min-width: 760px) and (max-width: 769.98px) and (max-height: 510.98px), (min-width: 770px) and (max-width: 779.98px) and (max-height: 522.98px), (min-width: 780px) and (max-width: 789.98px) and (max-height: 535.98px), (min-width: 790px) and (max-width: 799.98px) and (max-height: 547.98px), (min-width: 800px) and (max-width: 809.98px) and (max-height: 560.98px), (min-width: 810px) and (max-width: 819.98px) and (max-height: 572.98px), (min-width: 820px) and (max-width: 829.98px) and (max-height: 585.98px), (min-width: 830px) and (max-width: 839.98px) and (max-height: 597.98px), (min-width: 840px) and (max-width: 849.98px) and (max-height: 619.98px), (min-width: 850px) and (max-width: 859.98px) and (max-height: 631.98px), (min-width: 860px) and (max-width: 869.98px) and (max-height: 644.98px), (min-width: 870px) and (max-width: 879.98px) and (max-height: 656.98px), (min-width: 880px) and (max-width: 889.98px) and (max-height: 669.98px), (min-width: 890px) and (max-width: 899.98px) and (max-height: 681.98px), (min-width: 900px) and (max-width: 909.98px) and (max-height: 694.98px), (min-width: 910px) and (max-width: 919.98px) and (max-height: 706.98px), (min-width: 920px) and (max-width: 924.98px) and (max-height: 713.98px), (min-width: 925px) and (max-width: 1199.98px) and (max-height: 381.98px), (min-width: 1200px) and (max-height: 431.98px)'
);
function getGalleryMode() {
  return sliderMedia.matches ? 'slider' : 'grid';
}

// Initialize based on gallery mode
function initGallery() {
  if (getGalleryMode() === 'slider') {
    // Slider: Initialize Swiper
    if (!swiperInstance) {
      initMobileSwiper();
    }
  } else {
    // Grid: Destroy Swiper if exists, use CSS Grid
    if (swiperInstance) {
      swiperInstance.destroy(true, true);
      swiperInstance = null;
    }
    initDesktopGrid();
  }
}

// Mobile Swiper initialization
function initMobileSwiper() {
  // Add swiper class for mobile (prevents auto-init on desktop)
  swiperContainer.classList.add('swiper');

  // View More is grid-only; keep it `hidden` here so it can't flash on reload.
  const viewMoreBtn = swiperContainer.querySelector('.gallery-view-more-btn');
  if (viewMoreBtn) viewMoreBtn.hidden = true;

  // Cylindo's close (X) button is an absolute overlay - safe to hide without
  // affecting layout, unlike the cylindo-container itself (see initDesktopGrid).
  const closeCylindoBtn = document.querySelector('.close-button-cylindo');
  const imageGalleryEl = document.querySelector('.image-gallery');
  if (closeCylindoBtn && !imageGalleryEl?.classList.contains('open-cylindo')) {
    closeCylindoBtn.hidden = true;
  }

  // Images past the initial visible count only get a real src when View More
  // runs it, and that only exists in grid mode - slider mode has no such button,
  // so load them all now instead of leaving them as blank placeholders.
  loadPlaceholderImages(true);

  // Get slide count from Liquid (server-side, no DOM query needed)
  const slideCount = parseInt(swiperContainer.dataset.slideCount || '0');
  
  // Only enable loop if there are 2+ slides (Swiper requirement)
  const enableLoop = slideCount >= 2;

  // Before Swiper starts, the CSS lets the row scroll natively (scroll-snap).
  // If the shopper already swiped, start Swiper on that photo; the native
  // scroll position is dropped once Swiper takes over the row.
  const wrapper = swiperContainer.querySelector('.swiper-wrapper');
  let initialSlide = 0;
  if (wrapper && wrapper.clientWidth > 0) {
    initialSlide = Math.round(wrapper.scrollLeft / wrapper.clientWidth);
    wrapper.scrollLeft = 0;
  }

  swiperInstance = new Swiper('.product-media-display', {
    initialSlide,
    slidesPerView: 1,
    spaceBetween: 0,
    loop: enableLoop,
    loopAdditionalSlides: enableLoop ? 1 : 0,
    zoom: {
      maxRatio: 3,
      minRatio: 1,
      toggle: true,
    },
    scrollbar: {
      el: '.swiper-scrollbar',
      hide: true,
    },
    on: {
      // Pinch / double-tap zoom: fetch a sharp enough file for that photo.
      zoomChange(swiper, scale, imageEl, slideEl) {
        if (scale > 1) sharpenZoomedImage(slideEl);
      },
    },
  });
}

// Desktop Grid initialization
function initDesktopGrid() {
  // Remove ALL Swiper classes (prevents Swiper from initializing)
  swiperContainer.classList.remove('swiper', 'swiper-initialized', 'swiper-horizontal');
  const wrapper = swiperContainer.querySelector('.swiper-wrapper');
  if (wrapper) {
    wrapper.style.transform = 'none';
  }

  // Grid mode always shows View More (if it exists). The Cylindo viewer's
  // own visibility stays CSS-only (no `hidden` toggle) - unlike this
  // absolutely-positioned button, it sits in normal document flow, so
  // JS-toggling it after other content has painted would shift the page.
  const viewMoreBtn = swiperContainer.querySelector('.gallery-view-more-btn');
  if (viewMoreBtn) viewMoreBtn.hidden = false;

  // Desktop zoom and drag functionality (listeners are added once only)
  if (!desktopZoomReady) {
    setupDesktopZoomAndDrag();
    desktopZoomReady = true;
  }
}
let desktopZoomReady = false;

// Desktop zoom and drag setup
function setupDesktopZoomAndDrag() {
  let isDragging = false;
  let draggedSlide = null;
  let startX = 0;
  let startY = 0;
  let translateX = 0;
  let translateY = 0;
  
  // Function to zoom in an image (progressive: 1x → 2x → 3x)
  function zoomIn(slide) {
    if (!slide) return;
    
    const zoomContainer = slide.querySelector('.swiper-zoom-container');
    if (!zoomContainer) return;
    
    // Get current zoom level (default is 1)
    const currentZoom = parseFloat(slide.dataset.zoomLevel || '1');
    
    // Calculate next zoom level
    let nextZoom;
    if (currentZoom === 1) {
      nextZoom = 2;
    } else if (currentZoom === 2) {
      nextZoom = 3;
    } else {
      nextZoom = 3; // Already at max
    }
    
    // First, reset any other zoomed slides
    const allSlides = swiperContainer.querySelectorAll('.swiper-slide-zoomed');
    allSlides.forEach(zoomedSlide => {
      if (zoomedSlide !== slide) {
        const zoomedContainer = zoomedSlide.querySelector('.swiper-zoom-container');
        const zoomOutBtn = zoomedSlide.querySelector('.gallery-zoom-out');
        const zoomInBtn = zoomedSlide.querySelector('.gallery-zoom-in');
        if (zoomedContainer) {
          zoomedContainer.style.transition = 'transform 300ms ease';
          zoomedContainer.style.transform = 'translate3d(0px, 0px, 0px) scale(1)';
          zoomedSlide.classList.remove('swiper-slide-zoomed');
          zoomedSlide.dataset.translateX = '0';
          zoomedSlide.dataset.translateY = '0';
          zoomedSlide.dataset.zoomLevel = '1';
          
          // Reset button states
          if (zoomOutBtn) {
            zoomOutBtn.style.opacity = '0.3';
            zoomOutBtn.style.pointerEvents = 'none';
          }
          if (zoomInBtn) {
            zoomInBtn.style.opacity = '1';
            zoomInBtn.style.pointerEvents = 'auto';
          }
        }
      }
    });
    
    // Get current translate values to maintain pan position
    const currentTranslateX = parseFloat(slide.dataset.translateX || '0');
    const currentTranslateY = parseFloat(slide.dataset.translateY || '0');
    
    // Scale the translate values proportionally to the new zoom level
    const zoomRatio = nextZoom / currentZoom;
    let newTranslateX = currentTranslateX * zoomRatio;
    let newTranslateY = currentTranslateY * zoomRatio;
    
    // Calculate max allowed translate for new zoom level to prevent blank areas
    const containerRect = slide.getBoundingClientRect();
    const maxTranslateX = ((nextZoom - 1) * containerRect.width) / 2;
    const maxTranslateY = ((nextZoom - 1) * containerRect.height) / 2;
    
    // Constrain translate values to valid limits
    newTranslateX = Math.max(-maxTranslateX, Math.min(maxTranslateX, newTranslateX));
    newTranslateY = Math.max(-maxTranslateY, Math.min(maxTranslateY, newTranslateY));
    
    // Apply the zoom with maintained pan position
    slide.dataset.translateX = newTranslateX;
    slide.dataset.translateY = newTranslateY;
    slide.dataset.zoomLevel = nextZoom;
    
    zoomContainer.style.transition = 'transform 300ms ease';
    zoomContainer.style.transform = `translate3d(${newTranslateX}px, ${newTranslateY}px, 0px) scale(${nextZoom})`;
    slide.classList.add('swiper-slide-zoomed');
    
    // Update button states
    const zoomOutBtn = slide.querySelector('.gallery-zoom-out');
    const zoomInBtn = slide.querySelector('.gallery-zoom-in');
    
    // Zoom out button always active when zoomed
    if (zoomOutBtn) {
      zoomOutBtn.style.opacity = '1';
      zoomOutBtn.style.pointerEvents = 'auto';
    }
    
    // Zoom in button: active if not at max (3x), faded if at max
    if (zoomInBtn) {
      if (nextZoom === 3) {
        zoomInBtn.style.opacity = '0.3';
        zoomInBtn.style.pointerEvents = 'none';
      } else {
        zoomInBtn.style.opacity = '1';
        zoomInBtn.style.pointerEvents = 'auto';
      }
    }
    
    setTimeout(() => {
      zoomContainer.style.transition = '';
    }, 300);
  }
  
  // Zoom in button click handler
  swiperContainer.addEventListener('click', function(e) {
    if (e.target.closest('.gallery-zoom-in')) {
      e.stopPropagation();
      
      if (getGalleryMode() !== 'grid') return;

      const slide = e.target.closest('.swiper-slide');
      if (!slide) return;

      sharpenZoomedImage(slide);
      zoomIn(slide);
    }
  });
  
  // Zoom out button click handler (progressive: 3x → 2x → 1x)
  swiperContainer.addEventListener('click', function(e) {
    if (e.target.closest('.gallery-zoom-out')) {
      e.stopPropagation();
      
      const slide = e.target.closest('.swiper-slide');
      if (!slide) return;
      
      const zoomContainer = slide.querySelector('.swiper-zoom-container');
      const zoomOutBtn = slide.querySelector('.gallery-zoom-out');
      const zoomInBtn = slide.querySelector('.gallery-zoom-in');
      
      if (zoomContainer) {
        // Get current zoom level
        const currentZoom = parseFloat(slide.dataset.zoomLevel || '1');
        
        // Calculate next zoom level
        let nextZoom;
        if (currentZoom === 3) {
          nextZoom = 2;
        } else if (currentZoom === 2) {
          nextZoom = 1;
        } else {
          nextZoom = 1; // Already at min
        }
        
        // Get current translate values to maintain pan position
        const currentTranslateX = parseFloat(slide.dataset.translateX || '0');
        const currentTranslateY = parseFloat(slide.dataset.translateY || '0');
        
        // Scale the translate values proportionally to the new zoom level
        const zoomRatio = nextZoom / currentZoom;
        let newTranslateX = currentTranslateX * zoomRatio;
        let newTranslateY = currentTranslateY * zoomRatio;
        
        // Calculate max allowed translate for new zoom level to prevent blank areas
        const containerRect = slide.getBoundingClientRect();
        const maxTranslateX = ((nextZoom - 1) * containerRect.width) / 2;
        const maxTranslateY = ((nextZoom - 1) * containerRect.height) / 2;
        
        // Constrain translate values to valid limits
        newTranslateX = Math.max(-maxTranslateX, Math.min(maxTranslateX, newTranslateX));
        newTranslateY = Math.max(-maxTranslateY, Math.min(maxTranslateY, newTranslateY));
        
        // Apply zoom out with maintained pan position
        slide.dataset.translateX = newTranslateX;
        slide.dataset.translateY = newTranslateY;
        slide.dataset.zoomLevel = nextZoom;
        
        zoomContainer.style.transition = 'transform 300ms ease';
        zoomContainer.style.transform = `translate3d(${newTranslateX}px, ${newTranslateY}px, 0px) scale(${nextZoom})`;
        
        // Update class and button states
        if (nextZoom === 1) {
          slide.classList.remove('swiper-slide-zoomed');
          
          // At 1x: zoom-out disabled, zoom-in enabled
          if (zoomOutBtn) {
            zoomOutBtn.style.opacity = '0.3';
            zoomOutBtn.style.pointerEvents = 'none';
          }
          if (zoomInBtn) {
            zoomInBtn.style.opacity = '1';
            zoomInBtn.style.pointerEvents = 'auto';
          }
        } else {
          slide.classList.add('swiper-slide-zoomed');
          
          // At 2x: both buttons enabled
          if (zoomOutBtn) {
            zoomOutBtn.style.opacity = '1';
            zoomOutBtn.style.pointerEvents = 'auto';
          }
          if (zoomInBtn) {
            zoomInBtn.style.opacity = '1';
            zoomInBtn.style.pointerEvents = 'auto';
          }
        }
        
        setTimeout(() => {
          zoomContainer.style.transition = '';
        }, 300);
      }
    }
  });
  
  // Drag functionality for zoomed images
  const handleMouseDown = function(e) {
    if (getGalleryMode() !== 'grid') return;
    
    // Don't start drag if clicking on buttons
    if (e.target.closest('.gallery-controls-container') || e.target.closest('button')) {
      return;
    }
    
    const slide = e.target.closest('.swiper-slide');
    if (!slide || !slide.classList.contains('swiper-slide-zoomed')) return;
    
    const zoomContainer = slide.querySelector('.swiper-zoom-container');
    if (!zoomContainer) return;
    
    isDragging = true;
    draggedSlide = slide;
    
    // Get current translate values from dataset
    translateX = parseFloat(slide.dataset.translateX || '0');
    translateY = parseFloat(slide.dataset.translateY || '0');
    
    startX = e.clientX - translateX;
    startY = e.clientY - translateY;
    
    zoomContainer.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
    
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleMouseMove = function(e) {
    if (!isDragging || !draggedSlide) return;
    
    const zoomContainer = draggedSlide.querySelector('.swiper-zoom-container');
    if (!zoomContainer) return;
    
    const currentX = e.clientX - startX;
    const currentY = e.clientY - startY;
    
    // Get current zoom level from dataset
    const zoomLevel = parseFloat(draggedSlide.dataset.zoomLevel || '1');
    const containerRect = draggedSlide.getBoundingClientRect();
    
    // Calculate how much we can pan in each direction
    // Formula: (zoom - 1) * dimension / 2
    const maxTranslateX = ((zoomLevel - 1) * containerRect.width) / 2;
    const maxTranslateY = ((zoomLevel - 1) * containerRect.height) / 2;
    
    translateX = Math.max(-maxTranslateX, Math.min(maxTranslateX, currentX));
    translateY = Math.max(-maxTranslateY, Math.min(maxTranslateY, currentY));
    
    // Store translate values in dataset
    draggedSlide.dataset.translateX = translateX;
    draggedSlide.dataset.translateY = translateY;
    
    // Use current zoom level in transform
    zoomContainer.style.transform = `translate3d(${translateX}px, ${translateY}px, 0px) scale(${zoomLevel})`;
    e.preventDefault();
  };
  
  const handleMouseUp = function(e) {
    if (isDragging && draggedSlide) {
      const zoomContainer = draggedSlide.querySelector('.swiper-zoom-container');
      if (zoomContainer) {
        zoomContainer.style.cursor = 'grab';
      }
      
      document.body.style.userSelect = '';
      isDragging = false;
      draggedSlide = null;
    }
  };
  
  // Attach drag event listeners with passive where possible
  swiperContainer.addEventListener('mousedown', handleMouseDown);
  document.addEventListener('mousemove', handleMouseMove, { passive: false }); // Can't be passive (calls preventDefault)
  document.addEventListener('mouseup', handleMouseUp, { passive: true });
}

// Initialize gallery
initGallery();

// Mode switches (rotate, resize, foldable unfold) follow the media query
// straight away - no resize timer, no reading the window size.
sliderMedia.addEventListener('change', initGallery);

// Cylindo 360 view handlers - with null checks
const threeViewButton = document.querySelector('.three-view');
const closeCylindoButton = document.querySelector('.close-button-cylindo');
const imageGallery = document.querySelector('.image-gallery');

if (threeViewButton && imageGallery) {
  threeViewButton.addEventListener('click', function () {
    imageGallery.classList.add('open-cylindo');
    if (closeCylindoButton) closeCylindoButton.hidden = false;
  });
}

if (closeCylindoButton && imageGallery) {
  closeCylindoButton.addEventListener('click', function() {
    imageGallery.classList.remove('open-cylindo');
    closeCylindoButton.hidden = true;
  });
}


// Fullscreen gallery: fewest columns that keep every 4:5 photo within the
// screen height (staging D6). Fullscreen only exists once this script runs,
// so measuring here is fine; it re-measures while open if the window resizes.
const FULLSCREEN_GAP = 5;
let fullscreenFrame = 0;

function setFullscreenColumns() {
  const area = document.querySelector('.product__media-wrapper');
  if (!area) return;
  const cs = getComputedStyle(area);
  const width = area.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
  const height = area.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
  const widestPhoto = height * 0.8;
  const cols = Math.max(1, Math.ceil((width + FULLSCREEN_GAP) / (widestPhoto + FULLSCREEN_GAP)));
  swiperContainer.style.setProperty('--pdp-fs-cols', cols);

  // Fullscreen tiles are larger than grid tiles: tell the browser the real
  // slot so it fetches a sharp enough srcset width (restored on close).
  const tile = Math.ceil((width - (cols - 1) * FULLSCREEN_GAP) / cols);
  swiperContainer.querySelectorAll('img.product-image[sizes]').forEach((img) => {
    if (Number(img.dataset.zoomSize || 0) >= tile) return; // already sharp enough from a zoom
    if (!img.dataset.gridSizes) img.dataset.gridSizes = img.getAttribute('sizes');
    img.setAttribute('sizes', `${tile}px`);
  });
}

function restoreGridSizes() {
  swiperContainer.querySelectorAll('img.product-image[data-grid-sizes]').forEach((img) => {
    img.setAttribute('sizes', img.dataset.gridSizes);
    img.removeAttribute('data-grid-sizes');
  });
}

function onFullscreenResize() {
  cancelAnimationFrame(fullscreenFrame);
  fullscreenFrame = requestAnimationFrame(setFullscreenColumns);
}

document.addEventListener('click', function (e) {
  if (e.target.closest('.gallery-full-screen')) {
    e.stopPropagation();
    document.querySelector('body')?.classList.add('open-fullscreen');
    document.body.style.overflow = 'hidden';
    document.querySelector('.gallery-close-full-screen')?.classList.remove('hidden');

    // Fullscreen shows every photo: load any still waiting for View More.
    loadPlaceholderImages(false);
    setFullscreenColumns();
    window.addEventListener('resize', onFullscreenResize, { passive: true });
  }
});

document.addEventListener('click', function (e) {
   if (e.target.closest('.gallery-close-full-screen')) {
    document.querySelector('body')?.classList.remove('open-fullscreen');
      document.querySelector('.gallery-close-full-screen')?.classList.add('hidden');
      document.body.style.overflow = '';
      window.removeEventListener('resize', onFullscreenResize);
      swiperContainer.style.removeProperty('--pdp-fs-cols');
      restoreGridSizes();
   }
});

// View More (grid layouts only). Which photos it hides depends on the column
// count, so CSS does the hiding (.pdp-hN classes from Liquid); this only
// toggles .is-expanded and loads the photos that were left as placeholders.
const viewMoreBtn = document.querySelector('.gallery-view-more-btn');
if (viewMoreBtn) {
  viewMoreBtn.addEventListener('click', function() {
    const isExpanded = swiperContainer.classList.toggle('is-expanded');
    this.classList.toggle('expanded', isExpanded);

    if (isExpanded) {
      loadPlaceholderImages(true);
      this.querySelector('.view-more-text').textContent = 'Show Less';
    } else {
      this.querySelector('.view-more-text').textContent = 'View More';
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
}


  });